import { useEffect, useMemo, useState } from 'react';
import api from '../api/client';

/** Можно ли стартовать/выдать с текущими настройками (regex24 требует темы и режимы). */
export function isTrainerConfigReady(meta, config) {
  if (!meta) return false;
  const ui = meta.config_ui || {};
  if ((ui.topics || []).length > 0 && !(config.topics || []).length) return false;
  if ((ui.modes || []).length > 0 && !(config.modes || []).length) return false;
  return true;
}

/** Чекбоксы тем/режимов + число заданий; доп. поля из config_ui.fields. */
export default function TrainerConfigForm({
  meta,
  config,
  onChange,
  disabled = false,
}) {
  const ui = meta?.config_ui || {};
  const topics = ui.topics || [];
  const modes = ui.modes || [];
  const fields = ui.fields || [];

  function toggleList(key, id) {
    const cur = Array.isArray(config[key]) ? config[key] : [];
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    onChange({ ...config, [key]: next });
  }

  return (
    <div className="trainer-config-form">
      <label className="form-label" style={{ display: 'block' }}>
        Число заданий
        <input
          type="number"
          className="form-input"
          min={1}
          max={50}
          disabled={disabled}
          value={config.target_count ?? 10}
          onChange={(e) =>
            onChange({
              ...config,
              target_count: Math.max(1, Math.min(50, Number(e.target.value) || 1)),
            })
          }
        />
      </label>

      {fields.map((field) => {
        if (field.type === 'number') {
          const min = field.min ?? 1;
          const max = field.max ?? 50;
          return (
            <label
              key={field.key}
              className="form-label"
              style={{ display: 'block', marginTop: '0.75rem' }}
            >
              {field.label}
              <input
                type="number"
                className="form-input"
                min={min}
                max={max}
                disabled={disabled}
                value={config[field.key] ?? min}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  const clamped = Math.max(min, Math.min(max, Number.isFinite(n) ? n : min));
                  onChange({ ...config, [field.key]: clamped });
                }}
              />
            </label>
          );
        }
        if (field.type === 'checkbox') {
          return (
            <label
              key={field.key}
              className="checkbox-row"
              style={{ display: 'flex', gap: '0.5rem', marginTop: '0.65rem' }}
            >
              <input
                type="checkbox"
                disabled={disabled}
                checked={Boolean(config[field.key])}
                onChange={() => onChange({ ...config, [field.key]: !config[field.key] })}
              />
              {field.label}
            </label>
          );
        }
        return null;
      })}

      {topics.length > 0 && (
        <fieldset style={{ marginTop: '0.75rem', border: 'none', padding: 0 }}>
          <legend className="form-label" style={{ marginBottom: '0.35rem' }}>
            Темы
          </legend>
          {topics.map((t) => (
            <label key={t.id} className="checkbox-row" style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                type="checkbox"
                disabled={disabled}
                checked={(config.topics || []).includes(t.id)}
                onChange={() => toggleList('topics', t.id)}
              />
              {t.label}
            </label>
          ))}
        </fieldset>
      )}

      {modes.length > 0 && (
        <fieldset style={{ marginTop: '0.75rem', border: 'none', padding: 0 }}>
          <legend className="form-label" style={{ marginBottom: '0.35rem' }}>
            Режимы
          </legend>
          {modes.map((m) => (
            <label key={m.id} className="checkbox-row" style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                type="checkbox"
                disabled={disabled}
                checked={(config.modes || []).includes(m.id)}
                onChange={() => toggleList('modes', m.id)}
              />
              {m.label}
            </label>
          ))}
        </fieldset>
      )}
    </div>
  );
}

export function useTrainerKindMeta(slug) {
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(Boolean(slug));
  const [error, setError] = useState('');

  useEffect(() => {
    if (!slug) {
      setMeta(null);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await api.get(`/trainers/kinds/${slug}/`);
        if (!cancelled) {
          setMeta(res.data);
          setError('');
        }
      } catch {
        if (!cancelled) setError('Не удалось загрузить настройки тренажёра.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const defaultConfig = useMemo(() => meta?.default_config || {}, [meta]);
  return { meta, loading, error, defaultConfig };
}
